/**
 * A module for handling business profile settings and styles in the AIMI application.
 * This module provides functionality to manage business profile properties such as colors and fonts,
 * and includes the dynamic loading of Google Fonts.
 *
 * @module aimi_business_profile
 * @param {Object} aimi_business_profile - The main object for the business profile module where all the properties and methods are attached.
 * @param {jQuery} $ - The jQuery library used for DOM manipulation.
 * @param {Object} app - The main application object, providing access to global application functionality.
 * @param {undefined} undefined - An explicit undefined parameter used to safeguard against undefined global variable.
 */
(function (aimi_business_profile, $, app, undefined) {
  // Private object to hold module internals.
  let __this = {};

  /**
   * Stores business profile settings, initially empty and to be populated dynamically.
   */
  __this.business_profiles = {};

  /**
   * Cached jQuery selector for the document's body.
   */
  __this.$body = $('body');


  /**
   * Defines the API interface for interacting with business profiles in the app.
   * This includes methods for listing, updating, adding, deleting, and retrieving business profiles.
   * Each method corresponds to a specific HTTP request to the server's API endpoint for business profiles.
   *
   * @namespace app.apis
   * @property {function} business_profile - The business_profile function returns an object containing methods to interact with the business profile API.
   *
   * @returns {Object} Returns an object with methods to interact with business profiles, including:
   *                   - list: Retrieves a list of business profiles associated with the user.
   *                   - update: Updates a specific business profile by its ID.
   *                   - add: Adds a new business profile.
   *                   - delete: Deletes a business profile by its ID.
   *                   - get: Retrieves a specific business profile by its ID.
   */
  app.apis['business_profile'] = function () {
    return {
      /**
       * Retrieves a list of all business profiles associated with the current user.
       *
       * @returns {Promise} A promise that resolves with the list of business profiles.
       */
      'list': function () {
        return app.getRequest(`/api/user/personas`, {}, function () {
        }, function () {
        });
      },

      /**
       * Updates a specific business profile identified by its ID with the provided data.
       *
       * @param {string} id - The ID of the business profile to update.
       * @param {Object} requestData - The data to update the business profile with.
       * @returns {Promise} A promise that resolves when the update is complete.
       */
      'update': function (id, requestData) {
        return app.putRequest(`/api/user/personas/${id}`, requestData, function () {
        }, function () {
        });
      },

      /**
       * Adds a new business profile with the provided data.
       *
       * @param {Object} requestData - The data for the new business profile.
       * @returns {Promise} A promise that resolves when the business profile has been added.
       */
      'add': function (requestData) {
        return app.postRequest(`/api/user/personas`, requestData, function () {
        }, function () {
        });
      },

      'save': async function (requestData) {
        if (app.isEmpty(requestData.id)) {
          return this.add(requestData)
        } else {
          return this.update(requestData.id, requestData)
        }
      },

      /**
       * Deletes a business profile identified by its ID.
       *
       * @param {string} id - The ID of the business profile to delete.
       * @returns {Promise} A promise that resolves when the business profile is deleted.
       */
      'delete': function (id) {
        return app.deleteRequest(`/api/user/personas/${id}`, {}, function () {
        }, function (response) {
          app.notify('error', response);
        });
      },

      /**
       * Retrieves a specific business profile by its ID.
       *
       * @param {string} id - The ID of the business profile to retrieve.
       * @returns {Promise} A promise that resolves with the details of the business profile.
       */
      'get': function (id) {
        return app.getRequest(`/api/user/personas/${id}`, {}, function () {
        }, function () {
        });
      },

      /**
       * Get default or first persona
       * @returns {*}
       */
      getDefault: function () {
        let defaultPersona = personas[0];
        _.some(personas, function (persona) {
          if (parseInt(persona.default) === 1) {
            defaultPersona = persona;
            return true;
          }
        });
        return defaultPersona;
      },

      /**
       * Fetches campaigns associated with a given industry ID. This method facilitates retrieving
       * targeted campaign information relevant to a specific industry.
       *
       * @param {string} industryId - The unique identifier for the industry whose campaigns are to be fetched.
       * @returns {Promise} A promise that resolves to the campaign data for the specified industry ID.
       *                    The promise resolves with the campaign data on successful API call,
       *                    or rejects with an error if the request fails.
       */
      'getCampaignByIndustry': function (industryId) {
        return app.getRequest(`/api/campaign/campaign/by-industry/${industryId}`, {}, function () {
        }, function () {
        });
      },

      /**
       * Fetches a list of all industries. This method is used to retrieve comprehensive information
       * about different industries, potentially for filtering or categorizing campaigns.
       *
       * @returns {Promise} A promise that resolves to the list of industries. The promise resolves
       *                    with the industries data on successful API call, or rejects with an error
       *                    if the request fails.
       */
      'getIndustries': function () {
        return app.getRequest(`/api/campaign/industry`, {}, function () {
        }, function () {
        });
      }
    }
  }();

  document.addEventListener('DOMContentLoaded', function () {

    $(document).on("click.business-profile-actions", "[data-business-profile-action]", function (e) {
      const app = PreBuilt;

      const $this = $(this);
      const action = $this.attr('data-business-profile-action');
      const context = $this.attr('data-context');

      if (action === 'show-calendars') {
        let $listContainer;
        if(context) {
           $listContainer = $('[data-calendars]', $(context));
        } else {
           $listContainer = $('[data-calendars]');
        }

        const show = $this.attr('data-show');
        const id = $this.attr('data-business-profile-id');

        $(show).removeClass('d-none');
        __this.listMananger = new CalendarList(app, $listContainer, 'calender__list-row', false);
        __this.listMananger.renderByBusinessProfile(id);
      }

      if (action === 'update-calendar-posts') {
        const id = $this.attr('data-id');

        const updatePostsMessage = 'Are you sure you want to update all your posts? All your posts will be updated with your changes.';
        app.confirm('Update Posts', updatePostsMessage).then(function (response) {
          const $button = response.button;
          const confirm = response.confirm;
          const $modal = response.modal;
          // If the user confirms the deletion, proceed with the delete operation.
          if (confirm === true) {
            app.doButtonAction($button);
            const responsePosts = app.apis['calendar'].rebuild(id, true, true).then(function () {
              app.doButtonReset($button);
              app.hideModal($modal);
            });
          }
        })
      }

    });
  });

}(window.aimi_business_profile = window.aimi_business_profile || {}, jQuery, PreBuilt));
